import { useEffect, useState } from "react";
import axios from "axios";
import { CalendarDays, CheckCircle2, Loader2, Lock } from "lucide-react";
import { Logo } from "./Logo";
import { formatApiErrorDetail } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Reschedule() {
  const [info, setInfo] = useState(null);
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(true);
  const [newDate, setNewDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const booking = params.get("booking");
    const tok = params.get("token");
    setToken(tok || "");
    if (!booking || !tok) {
      setError("This page needs to be opened from your booking email.");
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const { data } = await axios.get(`${API}/bookings/info/${booking}`, { params: { token: tok } });
        setInfo(data);
      } catch (err) {
        setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't load this booking.");
      }
      setLoading(false);
    })();
  }, []);

  const tomorrow = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const params = new URLSearchParams(window.location.search);
      const { data } = await axios.post(`${API}/bookings/reschedule`, {
        booking_id: params.get("booking"),
        token: params.get("token"),
        new_date: newDate,
      });
      setDone(data.new_date);
      setInfo((i) => ({ ...i, game_date: data.new_date }));
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-6 py-16 font-body text-white" data-testid="reschedule-page">
      <div className="w-full max-w-md rounded-2xl border border-white/5 bg-surface p-8 text-center md:p-10">
        <span className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-surface-hover text-accentlight">
          {done ? <CheckCircle2 className="h-7 w-7 text-emerald-400" /> : <CalendarDays className="h-7 w-7" />}
        </span>

        {loading ? (
          <p className="flex items-center justify-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
            <Loader2 className="h-5 w-5 animate-spin text-accentlight" /> Loading booking…
          </p>
        ) : done ? (
          <>
            <h1 className="font-display text-3xl tracking-wider">DATE CHANGED</h1>
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Your game is now booked for <strong className="text-white">{done}</strong>. The calendar is updated
              and a fresh confirmation email is on its way.
            </p>
          </>
        ) : error && !info ? (
          <>
            <h1 className="font-display text-3xl tracking-wider">CAN'T LOAD BOOKING</h1>
            <p className="mt-3 text-sm leading-relaxed text-red-300">{error}</p>
          </>
        ) : info ? (
          <>
            <h1 className="font-display text-3xl tracking-wider">CHANGE DATE</h1>
            {info.status === "cancelled" || info.status === "denied" ? (
              <p className="mt-3 text-sm leading-relaxed text-amber-300">
                This booking was already {info.status}. Book a fresh game any time from the site.
              </p>
            ) : (
              <>
                <p className="mt-3 text-sm text-white/60">
                  {info.name}&rsquo;s {info.package || "booking"} — currently{" "}
                  <strong className="text-white">{info.game_date || "no date set"}</strong>
                  {info.deposit_paid ? " · deposit received" : ""}
                </p>
                <form onSubmit={submit} data-testid="reschedule-form">
                  <label htmlFor="rs-date" className="mt-6 block text-xs font-semibold uppercase tracking-[0.2em] text-white/50">
                    New game date
                  </label>
                  <input
                    id="rs-date"
                    type="date"
                    min={tomorrow}
                    required
                    value={newDate}
                    onChange={(e) => setNewDate(e.target.value)}
                    data-testid="reschedule-date-input"
                    className="mt-2 w-full rounded-lg border border-white/10 bg-ink px-4 py-3 text-sm text-white outline-none focus:border-accentlight"
                  />
                  {error && (
                    <p className="mt-3 text-xs text-red-300" data-testid="reschedule-error">
                      {error}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={busy}
                    data-testid="reschedule-submit-button"
                    className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight py-3 text-sm font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110 disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
                    Confirm new date
                  </button>
                  <p className="mt-3 text-[11px] leading-relaxed text-white/35">
                    The old date is released to the calendar and your new date is blocked instantly. A fresh
                    confirmation email follows.
                  </p>
                </form>
              </>
            )}
          </>
        ) : null}

        <a
          href="/"
          className="mt-6 block text-xs uppercase tracking-[0.2em] text-white/35 transition-colors hover:text-accentlight"
        >
          Back to site
        </a>
      </div>
    </div>
  );
}
